import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { Button, Col, DatePicker, Form, Row, Select } from 'antd';
import { EditFilled } from '@ant-design/icons';

import Drawer from 'components/Drawer';
import Table from 'components/Table';
import FormLabel from 'components/FormLabel';

import globalMessages from 'translations/messages';
import useUser from 'hooks/useUser';
import SettingsDrawer from 'components/Settings/SettingsDrawer';
import { LICENSE_SETTINGS } from 'shared/constants/settings';
import { LicenseInterval } from 'shared/services/prisma/types';
import { dayjs } from 'shared/utils/date';
import useFormatMessage from 'hooks/useFormatMessage';
import useGlobalContext from 'hooks/useGlobalContext';
import messages from './messages';

const fragment = gql`
  fragment dataSourceLicenseFragment on DataSourceLicense {
    id
    title
    uniqueTitle
    startDate
    endDate
    renewalDate
    interval
    license {
      id
      title
      uniqueTitle
    }
    modules {
      id
      module
      title
      uniqueTitle
    }
    overrides
  }
`;

const QUERY = gql`
  query ($id: ID!) {
    customer (id: $id) {
      id
      dataSources {
        id
        title
        licenses {
          ...dataSourceLicenseFragment
        }
      }
    }   
  }
  ${fragment}
`;

const UPDATE_LICENSE = gql`
  mutation($id: ID!, $input: DataSourceLicenseInput!) {
    updateDataSourceLicense(id: $id, input: $input) {
      ...dataSourceLicenseFragment
    }
  }
  ${fragment}
`;

const DELETE_LICENSE = gql`
  mutation($id: ID!) {
    deleteDataSourceLicense(id: $id) {
      id
    }
  }
`;

const LicenseForm = ({ form, onSubmit, dataSourceLicense }) => {
  let initialValues;

  if (dataSourceLicense) {
    initialValues = {
      startDate: dayjs(dataSourceLicense?.startDate),
      renewalDate: dataSourceLicense?.renewalDate ? dayjs(dataSourceLicense?.renewalDate) : null,
      endDate: dataSourceLicense?.endDate ? dayjs(dataSourceLicense?.endDate) : null,
      interval: dataSourceLicense?.interval,
    };
  } else {
    initialValues = {
      startDate: dayjs(),
      endDate: dayjs().add(1, 'year'),
      renewalDate: dayjs().add(9, 'months'),
      interval: LicenseInterval.Yearly,
    };
  }

  const $t = useFormatMessage();

  useEffect(() => {
    form.resetFields();
  }, [dataSourceLicense]);

  return (
    <Form layout="vertical" form={form} onFinish={onSubmit} initialValues={initialValues}>
      <Form.Item
        name="startDate"
        label={<FormLabel label={messages.startDateLabel} />}
      >
        <DatePicker allowClear={false} />
      </Form.Item>
      <Form.Item name="renewalDate" label={<FormLabel label={messages.renewalDateLabel} />}>
        <DatePicker />
      </Form.Item>
      <Form.Item name="endDate" label={<FormLabel label={messages.endDateLabel} />}>
        <DatePicker />
      </Form.Item>
      <Form.Item name="interval" label={<FormLabel label={messages.intervalLabel} />}>
        <Select>
          <Select.Option value={LicenseInterval.Yearly}>{$t(globalMessages.yearly)}</Select.Option>
          <Select.Option value={LicenseInterval.Monthly}>{$t(globalMessages.monthly)}</Select.Option>
        </Select>
      </Form.Item>
    </Form>
  );
};

const CustomerLicenses = ({ customer }) => {
  const [editLicense, setEditLicense] = useState();
  const [editLicenseSettings, setEditLicenseSettings] = useState();

  const { data, loading, refetch } = useQuery(QUERY, {
    variables: {
      id: customer.id,
    },
  });

  const [updateLicenseMutation, { loading: updating }] = useMutation(UPDATE_LICENSE);
  const [deleteLicenseMutation, { loading: deleting }] = useMutation(DELETE_LICENSE);

  const [editForm] = Form.useForm();
  const { message } = useGlobalContext();

  const { formatMessage: $t } = useIntl();
  const { isSuperUser } = useUser();

  const licenses = useMemo(() => data?.customer.dataSources.flatMap((dataSource) => dataSource.licenses.map((license) => ({
    ...license,
    dataSource,
  }))), [data]);

  const canEdit = isSuperUser;

  const columns = [
    {
      dataIndex: ['dataSource', 'title'],
      title: <FormattedMessage {...globalMessages.dataSource} />,
    },
    {
      dataIndex: 'title',
      title: <FormattedMessage {...globalMessages.license} />,
    },
    {
      dataIndex: 'uniqueTitle',
      title: <FormattedMessage {...messages.licenseVersionLabel} />,
      visible: false,
      inactive: !isSuperUser,
    },
    {
      dataIndex: 'startDate',
      title: <FormattedMessage {...messages.startDateLabel} />,
      type: 'date',
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'renewalDate',
      title: <FormattedMessage {...messages.renewalDateLabel} />,
      type: 'date',
    },
    {
      dataIndex: 'endDate',
      title: <FormattedMessage {...messages.endDateLabel} />,
      type: 'date',
    },
    {
      dataIndex: 'interval',
      title: <FormattedMessage {...messages.intervalLabel} />,
      render: (interval) => <FormattedMessage {...globalMessages[interval.toLowerCase()]} />,
    },
    {
      dataIndex: 'overrides',
      title: 'Overrides',
      render: (settings, activeLicense) => (
        <>
          {Object.keys(settings).length}
          {canEdit && <Button onClick={() => setEditLicenseSettings(activeLicense.id)} icon={<EditFilled />} style={{ marginLeft: '1em' }} />}
        </>
      ),
      align: 'right',
      visible: isSuperUser,
    },
  ];

  const onEdit = async (input) => {
    await updateLicenseMutation({
      variables: {
        id: editLicense.id,
        input,
      },
    });

    setEditLicense(false);
    message.success($t(globalMessages.successfullFormFeedback));
  };

  const onDelete = async () => {
    await deleteLicenseMutation({
      variables: {
        id: editLicense.id,
      },
    });

    refetch();
    setEditLicense(false);
  };

  const onCloseEditLicenseDrawer = () => {
    setEditLicenseSettings(false);
    refetch();
  };

  const actions = isSuperUser && (website => (
    <Button type="primary" icon={<EditFilled />} onClick={() => setEditLicense(website)}>
      <FormattedMessage {...globalMessages.edit} />
    </Button>
  ));

  return (
    <>
      <Table
        loading={loading}
        columns={columns}
        dataSource={licenses}
        actions={actions}
      />
      <Drawer
        title={<FormattedMessage {...messages.editLicense} />}
        open={editLicense}
        onClose={() => setEditLicense(false)}
        footer={(
          <Row justify="space-between">
            <Col>
              <Button
                onClick={editForm.submit}
                type="primary"
                loading={updating}
              >
                <FormattedMessage {...globalMessages.save} />
              </Button>
            </Col>
            <Col>
              <Button danger onClick={onDelete} loading={deleting}>
                <FormattedMessage {...globalMessages.delete} />
              </Button>
            </Col>
          </Row>
            )}
      >
        {editLicense && <LicenseForm dataSourceLicense={editLicense} form={editForm} onSubmit={onEdit} />}
      </Drawer>
      <SettingsDrawer open={!!editLicenseSettings} relationId={editLicenseSettings} settings={LICENSE_SETTINGS} onDone={onCloseEditLicenseDrawer} onCancel={onCloseEditLicenseDrawer} edit />
    </>
  );
};

export default CustomerLicenses;
